"use client";

import { useState, useRef, useEffect, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { OptimizedImage } from "@/components/ui/optimized-image";
import {
  DETAIL_BODY_IMAGE_WIDTH,
  optimizeBodyImages,
} from "@/lib/optimize-body-images";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { DEMOTED_HEADING_CLASSES, demotePartnerHeadings } from "@/lib/html/demote-headings";
import { useTranslations } from "next-intl";
import type { ComponentSection } from "@/lib/product-facts/component-sections";

export interface DetailSection {
  id: string;
  image_url: string;
  description: string;
  display_order: number;
}

interface ProductDescriptionProps {
  content: string;
  sections?: DetailSection[];
  /**
   * Per-language unified rich HTML produced by the admin TipTap editor.
   * When present, it takes precedence over the legacy `sections` array.
   */
  detailPageHtml?: string | null;
  /**
   * Server-rendered detail page body — already parses inline `<div data-block>`
   * placeholders into live product/partner card React nodes. When provided,
   * it takes precedence over local sanitize+render of `detailPageHtml`.
   */
  detailPageBody?: ReactNode;
  title?: string;
  productName?: string;
  /**
   * Set contents parsed from the legacy sections ("봄 구성품: …", see
   * `parseComponentSections`). Shown as text even when the detail HTML exists —
   * the HTML only carries them as a picture (hinata camping sets).
   */
  componentSections?: ComponentSection[];
}

const COLLAPSED_HEIGHT = 600; // px

/**
 * Render the unified per-language detail page HTML.
 *
 * Trust boundary: the HTML is authored by partners through the admin's TipTap
 * editor whose enabled extensions cap the output surface. We render as-is,
 * mirroring BlogPostBody's policy. (We previously sanitized via
 * isomorphic-dompurify, but that pulls in jsdom which fails to load on Vercel
 * due to a CJS-requires-ESM chain in html-encoding-sniffer / @exodus/bytes.)
 */
function DetailPageHtml({ html }: { html: string }) {
  return (
    <div
      className={`prose prose-sm sm:prose-base prose-zinc max-w-none prose-headings:tracking-tight prose-headings:my-0 prose-p:my-0 prose-img:my-0 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline prose-li:my-0 prose-ul:my-0 prose-ol:my-0 prose-blockquote:border-0 prose-blockquote:pl-0 prose-blockquote:not-italic prose-blockquote:font-normal prose-blockquote:my-0 wrap-break-word [&_blockquote]:border-0 [&_blockquote]:pl-0 [&_figure]:m-0 [&_:not(.not-prose,.not-prose_*)>img]:w-full [&_img]:rounded-none [&_em]:italic [&_em]:text-inherit [&_strong]:text-inherit [&_u]:text-inherit [&_s]:text-inherit [&_p:empty]:min-h-[1.5em] [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:h-auto [&_video]:w-full ${DEMOTED_HEADING_CLASSES}`}
      dangerouslySetInnerHTML={{
        __html: demotePartnerHeadings(optimizeBodyImages(html, DETAIL_BODY_IMAGE_WIDTH)),
      }}
    />
  );
}

export default function ProductDescription({
  content,
  sections = [],
  detailPageHtml,
  detailPageBody,
  title,
  productName,
  componentSections = [],
}: ProductDescriptionProps) {
  const t = useTranslations("product.description");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasDetailPageBody = detailPageBody != null;
  const hasDetailPageHtml =
    hasDetailPageBody ||
    (typeof detailPageHtml === "string" && detailPageHtml.trim() !== "");

  // Raw HTML — see DetailPageHtml's trust-boundary comment above.
  const rawDetailHtml = hasDetailPageHtml ? detailPageHtml ?? "" : "";

  // Sort sections by display_order (legacy fallback only)
  const sortedSections = [...sections].sort(
    (a, b) => a.display_order - b.display_order,
  );

  useEffect(() => {
    const checkOverflow = () => {
      if (containerRef.current) {
        const hasOverflow =
          containerRef.current.scrollHeight > COLLAPSED_HEIGHT;
        setIsOverflowing(hasOverflow);
      }
    };

    checkOverflow();
    // Re-check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [content, sections, rawDetailHtml]);

  if (
    !content &&
    !hasDetailPageHtml &&
    (!sections || sections.length === 0)
  ) {
    return null;
  }

  return (
    <div className="pb-6">
      {/* Section Title */}
      <h2 className="text-lg sm:text-xl font-semibold mb-4">
        {title || t("title")}
      </h2>

      {/* Content Container */}
      <div
        ref={containerRef}
        className={cn(
          "relative overflow-hidden transition-all duration-300",
          !isExpanded && isOverflowing && `max-h-[${COLLAPSED_HEIGHT}px]`,
        )}
        style={{
          maxHeight:
            !isExpanded && isOverflowing ? COLLAPSED_HEIGHT : undefined,
        }}
      >
        {/* Description Text */}
        {content && (
          <div className="prose prose-sm sm:prose-base prose-zinc mb-6 max-w-none wrap-break-word prose-headings:my-3 prose-headings:font-semibold prose-headings:tracking-tight prose-p:my-3 prose-p:leading-7 prose-p:text-gray-800 prose-li:my-1 prose-li:leading-6 prose-img:my-0 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:my-0 prose-blockquote:border-0 prose-blockquote:pl-0 prose-blockquote:font-normal prose-blockquote:not-italic [&_blockquote]:border-0 [&_blockquote]:pl-0 [&_img]:rounded-none [&_em]:italic [&_em]:text-inherit [&_strong]:text-inherit [&_u]:text-inherit [&_s]:text-inherit [&_p:empty]:min-h-[1.5em]">
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>{content}</ReactMarkdown>
          </div>
        )}

        {/* Set contents per season — text the detail HTML only shows as an
            image. Legacy sections below already print their own text. */}
        {hasDetailPageHtml && componentSections.length > 0 && (
          <ul className="mb-6 space-y-2 text-sm leading-6 text-gray-800 wrap-break-word sm:text-base sm:leading-7">
            {componentSections.map((section) => (
              <li key={section.label}>
                <span className="font-medium text-gray-900">{section.label}</span>
                {" — "}
                {section.items.join(" · ")}
              </li>
            ))}
          </ul>
        )}

        {/* New unified detail page — prefer server-rendered body (which includes
            live product/partner embed cards). Falls back to in-place HTML
            sanitization when only the raw HTML is provided. */}
        {hasDetailPageBody ? (
          detailPageBody
        ) : hasDetailPageHtml ? (
          <DetailPageHtml html={rawDetailHtml} />
        ) : null}

        {/* Legacy detail sections — only when new HTML is absent */}
        {!hasDetailPageHtml && sortedSections.length > 0 && (
          <div className="space-y-8 overflow-hidden">
            {sortedSections.map((section) => {
              const [sectionTitle] = section.description.split(/:\s*/, 2);
              return <div key={section.id} className="space-y-3">
                {/* Image */}
                <div className="relative w-full overflow-hidden bg-gray-100">
                  <OptimizedImage
                    src={section.image_url}
                    alt={sectionTitle || productName || ""}
                    width={0}
                    height={0}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 800px"
                    className="h-auto"
                    style={{ width: '100%', maxWidth: '100%' }}
                  />
                </div>

                {/* Description */}
                {section.description && (
                  <p className="pl-1 text-sm leading-7 text-gray-800 whitespace-pre-wrap wrap-break-word sm:text-base">
                    {section.description}
                  </p>
                )}
              </div>
            })}
          </div>
        )}

        {/* Gradient Overlay when collapsed */}
        {!isExpanded && isOverflowing && (
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-linear-to-t from-white via-white/90 to-transparent pointer-events-none" />
        )}
      </div>

      {/* Expand/Collapse Button - Full Width */}
      {isOverflowing && (
        <div className="mt-4">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="outline"
            className="w-full py-6 rounded-xl border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all"
          >
            {isExpanded ? (
              <>
                {t("collapse")}
                <ChevronUp className="ml-2 h-5 w-5" />
              </>
            ) : (
              <>
                {t("showMore")}
                <ChevronDown className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
